import React, { useState, useEffect, useRef, useCallback } from "react";
import * as Mui from "@mui/material";
import { useFetch } from "services/hooks/useFetch";
import {
  themes,
  scrollTop,
  tableRowSize,
  stickyHeader,
  drawerStyle,
} from "services/constants";
import { useSelector } from "react-redux";
import { InfoMessage } from "components/CustomComponents/CustomButton";
import { Loader } from "components/Loader";
import { Typography, Box, Paper } from "@mui/material";
import { CustomFilter } from "components/CustomComponents/CustomFilter";
import { checkEditorPermission } from "services/utils/HelperFunction";
import { salaryRevisionTableHeader, salaryTableHeader } from "pages/HR/Headers";
import { ViewSalaryTemplate } from "./ViewSalaryTemplate";
import { useDispatch } from "react-redux";
import { setScreenUserId } from "services/Redux/HRPortal";
import { useSalaryRevise } from "../../Configuration/SalaryRevise/SalaryReviseContext";

export const SalaryTable = () => {
  const { domain, topbarCompanyId, userScreenAccess } = useSelector(
    (state) => state.tokenReducer,
  );
  const containerRef = useRef(null);
  const [salaryRevisionDetails, setSalaryRevisionDetails] = useState([]);
  const [getRevisionData, setGetRevisionData] = useState("");
  const { handleLoader, setHandleLoader } = useSalaryRevise();

  let check_access = checkEditorPermission(
    userScreenAccess?.module_details,
    "Salary Configuration",
    "Salary Table",
  );

  const {
    result: revisionData,
    setLoad: reCallApi,
    loading,
  } = useFetch(
    `${domain}hrzone/salary/salary_table/salary-table/?company_id=${topbarCompanyId}`,
  );
  const dispatch = useDispatch();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [isViewPanelOpen, setIsViewPanelOpen] = useState(false);
  const [getUserId, setGetUserId] = useState([]);
  const handleFilteredDataChange = useCallback(
    (filteredData) => {
      setSalaryRevisionDetails(filteredData);
      if (page * rowsPerPage >= filteredData.length) setPage(0);
    },
    [page, rowsPerPage],
  );

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  useEffect(() => {
    scrollTop(containerRef);
  }, [page]);

  useEffect(() => {
    if (topbarCompanyId) {
      reCallApi(true);
      setHandleLoader(true);
    }
  }, [reCallApi, setHandleLoader, topbarCompanyId]);

  const handleViewClosePanel = () => {
    setIsViewPanelOpen(false);
  };

  return handleLoader && loading ? (
    <Loader />
  ) : (
    <React.Fragment>
      <Box>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            width: "100%",
            mb: 1,
          }}
        >
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 600 }}>
              Salary Table
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Create a predefined salary structure by grouping components.
            </Typography>
          </Box>
        </Box>

        <Paper sx={{ width: "100%", height: "78vh" }}>
          <CustomFilter
            title={"Salary Table Records"}
            filterHeaders={salaryTableHeader}
            data={revisionData}
            filteredData={salaryRevisionDetails}
            onFilteredDataChange={handleFilteredDataChange}
            screenAccess={check_access}
          />

          <Mui.Grid sx={{ ml: 2, mr: 2 }}>
            <Mui.TableContainer ref={containerRef} sx={{ maxHeight: 590 }}>
              <Mui.Table className="border">
                <Mui.TableHead sx={stickyHeader}>
                  <Mui.TableRow sx={tableRowSize}>
                    {salaryRevisionTableHeader.map((header) => (
                      <Mui.TableCell
                        key={header.name}
                        align="center"
                        colSpan={header.children ? header.children.length : 1}
                        rowSpan={header.children ? 1 : 2}
                        sx={{
                          color: themes.headLine,
                          fontWeight: "bold",
                          border: "1px solid #ddd",
                        }}
                      >
                        {header.name}
                      </Mui.TableCell>
                    ))}
                  </Mui.TableRow>
                  <Mui.TableRow sx={tableRowSize}>
                    {salaryRevisionTableHeader
                      .filter((header) => header.children)
                      .flatMap((header) =>
                        header.children.map((child) => (
                          <Mui.TableCell
                            key={child.key}
                            align="center"
                            sx={{
                              color: themes.headLine,
                              fontWeight: "bold",
                              border: "1px solid #ddd",
                            }}
                          >
                            {child.name}
                          </Mui.TableCell>
                        )),
                      )}
                  </Mui.TableRow>
                </Mui.TableHead>
                <Mui.TableBody>
                  {salaryRevisionDetails?.length === 0 ? (
                    <InfoMessage dataLength={revisionData} />
                  ) : (
                    salaryRevisionDetails
                      ?.slice(
                        page * rowsPerPage,
                        page * rowsPerPage + rowsPerPage,
                      )
                      ?.map((value) => (
                        <Mui.TableRow sx={tableRowSize}>
                          {salaryRevisionTableHeader.flatMap((header) => {
                            const salarySource =
                              header.name === "Old Salary"
                                ? value?.previous_salary
                                : header.name === "New Salary"
                                  ? value?.new_salary
                                  : null;

                            return header.children
                              ? header.children.map((child) => (
                                  <Mui.TableCell
                                    key={`${header.name}-${child.key}`}
                                    align="center"
                                    sx={{ border: "1px solid #ddd" }}
                                    onClick={() => {
                                      setIsViewPanelOpen(true);
                                      setGetUserId(value?.user_id);
                                      dispatch(setScreenUserId(value?.user_id));
                                      setGetRevisionData(value);
                                    }}
                                  >
                                    {salarySource?.[child.key] ?? "-"}
                                  </Mui.TableCell>
                                ))
                              : [
                                  <Mui.TableCell
                                    key={header.key}
                                    align="center"
                                    sx={{ border: "1px solid #ddd" }}
                                    onClick={() => {
                                      setIsViewPanelOpen(true);
                                      setGetUserId(value?.user_id);
                                      dispatch(setScreenUserId(value?.user_id));
                                    }}
                                  >
                                    {typeof value?.[header.key] === "boolean"
                                      ? value[header.key]
                                        ? "Enabled"
                                        : "Disabled"
                                      : (value?.[header.key] ?? "-")}
                                  </Mui.TableCell>,
                                ];
                          })}
                        </Mui.TableRow>
                      ))
                  )}
                </Mui.TableBody>
              </Mui.Table>
            </Mui.TableContainer>
            <Box sx={{ mt: 1 }}>
              {salaryRevisionDetails?.length > 25 && (
                <Mui.TablePagination
                  className="custom-pagination"
                  component="div"
                  rowsPerPageOptions={[25, 50, 75, 100]}
                  count={salaryRevisionDetails?.length}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                />
              )}
            </Box>
          </Mui.Grid>
        </Paper>
      </Box>
      <Mui.Drawer
        anchor="right"
        open={isViewPanelOpen}
        onClose={() => {
          handleViewClosePanel();
        }}
        sx={drawerStyle}
      >
        <ViewSalaryTemplate
          onClose={handleViewClosePanel}
          reCallApi={reCallApi}
          getUserId={getUserId}
          getRevisionData={getRevisionData}
          screenAccess={check_access}
        />
      </Mui.Drawer>
    </React.Fragment>
  );
};
